import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectedFacilityAtom } from '@/store/facilityAtom';
import { siteGroupExperienceAtom } from '@/store/siteGroupExperienceAtom';
import { lookupService } from '@/services/lookupService';

/**
 * Resolves the site-group experience (theme tokens, feature flag overrides,
 * practice info) for the currently selected facility and writes it to
 * siteGroupExperienceAtom. No-op when no facility is selected. Cached for
 * 15 min to match the backend's IMemoryCache TTL on GetSiteGroupExperienceAsync.
 *
 * Only applies when the response's siteGroupCode matches the selected facility —
 * otherwise a slow PEI fetch could overwrite a just-applied flyer SiteGroup (NSA)
 * theme after `/mobile/{guid}` entry.
 *
 * Stale-experience clear on facilityId change: required for Self-Reg
 * `/Schedule/?SiteGroup=` interrupts. Facility can flip to PEI while
 * `site_group_experience` still holds the prior SiteGroup palette; without clearing,
 * demos look like "PEI URL but North theme" until/unless a fetch wins the race.
 * Keep in sync with `clearStaleExperience` in `lib/bootstrapSiteGroup.ts`.
 */
export function useSiteGroupExperience() {
  const facility = useAtomValue(selectedFacilityAtom);
  const setExperience = useSetAtom(siteGroupExperienceAtom);
  const facilityId = facility?.id?.trim() || '';

  const query = useQuery({
    queryKey: ['siteGroupExperience', facilityId],
    queryFn: () => lookupService.getSiteGroupExperience(facilityId),
    enabled: !!facilityId,
    staleTime: 15 * 60 * 1000,
  });

  // Drop mismatched cached theme as soon as facility changes (Self-Reg / Mobile interrupt).
  useEffect(() => {
    if (!facilityId) return;
    setExperience((prev) => {
      if (!prev) return prev;
      if (prev.siteGroupCode.trim().toLowerCase() === facilityId.toLowerCase()) return prev;
      try {
        localStorage.removeItem('site_group_experience');
      } catch { /* ignore */ }
      return null;
    });
  }, [facilityId, setExperience]);

  useEffect(() => {
    const data = query.data;
    if (!data || !facilityId) return;
    if (data.siteGroupCode.trim().toLowerCase() !== facilityId.toLowerCase()) return;
    setExperience(data);
  }, [query.data, setExperience, facilityId]);

  return query;
}
